import { AutocompleteOption, PoolAutocomplete } from '@synergy/components-next';
import { Box, ButtonBase, FormHelperText, Grid, IconButton, Stack, Theme, useTheme } from '@mui/material';
import { EditIcon } from '@synergy/theme';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolTextArea, PoolTypography } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';

import { DeliveryTermsModal } from './terms-modal/delivery-terms-modal';
import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import { supplierConditionsSelector } from '../store/supplier-conditions-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    term: {
        display: 'flex',
        alignItems: 'flex-start',

        '& > div:first-child': {
            flexGrow: 1,
            marginRight: theme.spacing(2),
        },
    },
}));

export const SupplierDetailConditionsPurchaseTerms = () => {
    const classes = useStyles();
    const theme = useTheme();
    const supplierConditions = useAppSelector(supplierConditionsSelector);
    const { values, setFieldValue } = useFormikContext<SupplierConditionsFormValues>();
    const [isDeliveryTermsModalVisible, setIsDeliveryTermsModalVisible] = useState(false);
    const [writingOwnText, setWritingOwnText] = useState(false);

    // The text that applies without one of the supplier's own, by the rule
    // the documents and the portal follow. It was a fixed placeholder, out of
    // step with the settings and unseen under a label set above the field —
    // the field read as empty (#2326 R-242).
    const standardText = supplierConditions.baseline?.defaultConditionsOfPurchase;
    // An empty field with a note below read as "missing, please fill in", and
    // the standard text got copied in by hand, cut off from the central one.
    // Without a text of its own the field shows the standard, read only; only
    // "Eigenen Text erfassen" turns it into an input (#2326 R-253).
    const hasOwnText = !!values.conditionsOfPurchase?.trim();
    const showsStandard = !hasOwnText && !writingOwnText && !!standardText;

    // Several hundred terms, many of them one supplier's own: a plain list had
    // to be scrolled through to find one. Typing a word of it narrows the list.
    const deliveryTermOptions: AutocompleteOption<number>[] = supplierConditions.deliveryTerms.map(deliveryTerm => ({
        value: deliveryTerm.id,
        displayValue: deliveryTerm.value,
    }));

    return (
        <Grid container={true} spacing={2}>
            <Grid item={true} xs={12}>
                <PoolTypography styleType="sectionTitleNew" id="app.contactsAndOrganizations.detail.purchaseTerms" />
            </Grid>

            <Grid item={true} xs={12} className={classes.term}>
                <Field name="deliveryTermId">
                    {({ field, form, meta }: FieldProps<number>) => (
                        <Stack>
                            <PoolAutocomplete<number>
                                options={deliveryTermOptions}
                                value={field.value}
                                onChange={id => form.setFieldValue(field.name, id)}
                                label={<FormattedMessage id="app.contactsAndOrganizations.detail.deliveryTerm" />}
                                // Not mandatory: 1.978 of 3.434 suppliers carry
                                // none, and the GF decided against it (#2212, R-28).
                                required={false}
                                error={!!meta.touched && !!meta.error}
                            />
                            {meta.error && meta.touched && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </Stack>
                    )}
                </Field>
                <IconButton onClick={() => setIsDeliveryTermsModalVisible(true)} sx={{ mt: 0.75 }}>
                    <EditIcon htmlColor={theme.palette.primary.main} />
                </IconButton>
            </Grid>

            <Grid item={true} xs={12}>
                {showsStandard ? (
                    // Read only, yet a text that applies: faded to 60 % it reached
                    // 4.3:1 and passed for a placeholder. Grey without the fade
                    // keeps it set apart and readable (#2326 R-253).
                    <Box
                        sx={{
                            // Specific enough to win over the read-only fade of PoolTextArea.
                            '& .MuiInputBase-root textarea.MuiInputBase-input': {
                                opacity: 1,
                                color: '#4B5563',
                                WebkitTextFillColor: '#4B5563',
                            },
                        }}
                    >
                        <PoolTextArea
                            value={standardText ?? ''}
                            readOnly={true}
                            fullWidth={true}
                            rows="2"
                            rowsMax="50"
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.conditionsOfPurchase" />}
                        />
                    </Box>
                ) : (
                    <Field name="conditionsOfPurchase">
                        {({ field, meta }: FieldProps<string>) => (
                            <PoolTextArea
                                {...field}
                                fullWidth={true}
                                rows="2"
                                rowsMax="50"
                                autoFocus={writingOwnText && !hasOwnText}
                                label={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.conditionsOfPurchase" />
                                }
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            />
                        )}
                    </Field>
                )}
                {standardText && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
                        <Box
                            component="span"
                            sx={{
                                padding: '1px 8px',
                                borderRadius: '999px',
                                border: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                            }}
                        >
                            <FormattedMessage
                                id={
                                    showsStandard
                                        ? 'app.contactsAndOrganizations.detail.conditionsOfPurchase.standardTag'
                                        : 'app.contactsAndOrganizations.detail.conditionsOfPurchase.ownTag'
                                }
                            />
                        </Box>
                        <ButtonBase
                            onClick={() => {
                                if (showsStandard) {
                                    setWritingOwnText(true);
                                    return;
                                }
                                // Emptied, the server keeps no text and the standard applies again.
                                setFieldValue('conditionsOfPurchase', '');
                                setWritingOwnText(false);
                            }}
                            sx={{ fontSize: '12px', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                        >
                            <FormattedMessage
                                id={
                                    showsStandard
                                        ? 'app.contactsAndOrganizations.detail.conditionsOfPurchase.writeOwn'
                                        : 'app.contactsAndOrganizations.detail.conditionsOfPurchase.resetToStandard'
                                }
                            />
                        </ButtonBase>
                    </Box>
                )}
            </Grid>

            <DeliveryTermsModal
                isVisible={isDeliveryTermsModalVisible}
                onClose={() => setIsDeliveryTermsModalVisible(false)}
            />
        </Grid>
    );
};
